// One member's spending cap, inline in the roster (RFC-1372 C3).
//
// Gated by `canSetAllowances`, and the gate HIDES rather than disables —
// consistent with the rest of this panel (§6: a Member "must not see controls
// they cannot use"). A Member sees nothing here at all, not a greyed-out form.
//
// ── Why the cap is stated in three parts ───────────────────────────────────
//
// The three limit types are mutually exclusive in the schema, so the form
// cannot offer a single "amount" field with an optional period: `fixed` with a
// period, or `no_limit` with an amount, are rejected by both the API and the
// database. Choosing the type first and revealing only the fields that type
// permits makes the invalid combinations unrepresentable rather than merely
// refused.
//
// ── The number on screen is remaining, not the limit ───────────────────────
//
// `remaining` already accounts for a recurring period that has rolled over, so
// it is what the member can actually spend right now. Showing the limit alone
// would read as available budget on the first of the month and be wrong for
// the rest of it.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type {
  AllowanceLimitType,
  AllowancePeriod,
  MemberAllowance,
  TeamRole,
} from '../../api/teams'
import { useClearMemberAllowance, useSetMemberAllowance } from '../../hooks/useTeams'
import ListboxSelect from './ListboxSelect'
import { cn } from '../../lib/utils'
import { trackAllowanceAction } from '../../analytics/interactionAnalytics'

interface MemberAllowanceControlProps {
  teamId: string
  userId: string
  /** The member's current cap, or null when uncapped (the default). */
  allowance: MemberAllowance | null
  /** Human label for the member, for aria text. */
  memberLabel: string
  onError: (message: string | null) => void
  /** Analytics only: the acting user's role and the capped member's role. */
  actorRole?: TeamRole
  targetRole?: TeamRole
}

const PERIODS: readonly AllowancePeriod[] = ['daily', 'weekly', 'monthly']
const LIMIT_TYPES: readonly AllowanceLimitType[] = ['no_limit', 'fixed', 'recurring']

export default function MemberAllowanceControl({
  teamId,
  userId,
  allowance,
  memberLabel,
  onError,
  actorRole,
  targetRole,
}: MemberAllowanceControlProps) {
  const { t } = useTranslation('settings')
  const setAllowance = useSetMemberAllowance()
  const clearAllowance = useClearMemberAllowance()

  const [editing, setEditing] = useState(false)
  const [limitType, setLimitType] = useState<AllowanceLimitType>(
    allowance?.limitType ?? 'recurring',
  )
  const [amount, setAmount] = useState<string>(
    allowance?.limitAmount === null || allowance?.limitAmount === undefined
      ? ''
      : String(allowance.limitAmount),
  )
  const [period, setPeriod] = useState<AllowancePeriod>(allowance?.period ?? 'monthly')

  const pending = setAllowance.isPending || clearAllowance.isPending

  const summary = (): string => {
    if (allowance === null) return t('settingsModal.teams.allowance.unlimited')
    if (allowance.limitType === 'no_limit') {
      return t('settingsModal.teams.allowance.unlimited')
    }
    // `remaining` is never null for fixed/recurring, but the type permits it;
    // fall back to the limit rather than rendering "null credits".
    const remaining = allowance.remaining ?? allowance.limitAmount ?? 0
    if (allowance.limitType === 'fixed') {
      return t('settingsModal.teams.allowance.summaryFixed', {
        remaining,
        limit: allowance.limitAmount ?? 0,
      })
    }
    return t('settingsModal.teams.allowance.summaryRecurring', {
      remaining,
      limit: allowance.limitAmount ?? 0,
      // The NOUN ("month"), not the selector's adverb ("Monthly") — the summary
      // reads "left this month", and "left this Monthly" is not a sentence.
      period: t(`settingsModal.teams.allowance.periodNoun.${allowance.period ?? 'monthly'}`),
    })
  }

  const submit = () => {
    onError(null)
    // `Number`, not `parseInt`: parseInt("1.5") is 1 and parseInt("12abc") is
    // 12, so either would save a cap the admin did not type. Credits are whole,
    // so a fractional or trailing-garbage entry is a mistake to report, not to
    // round.
    //
    // Zero is a legitimate cap ("may not spend"), so the guard tests NaN,
    // non-integers and negatives — never falsiness.
    const parsed = Number(amount)
    if (limitType !== 'no_limit') {
      if (amount.trim() === '' || !Number.isInteger(parsed) || parsed < 0) {
        onError(t('settingsModal.teams.allowance.invalidAmount'))
        return
      }
    }
    const limitAmount = limitType === 'no_limit' ? null : parsed
    const savedPeriod = limitType === 'recurring' ? period : null
    const trackSet = (ok: boolean) => trackAllowanceAction({
      teamId, action: 'set', ok, limitType, previousLimitType: allowance?.limitType ?? 'no_limit',
      period: savedPeriod, limitAmount, targetRole, actorRole,
    })
    setAllowance.mutate(
      {
        teamId,
        userId,
        limitType,
        limitAmount,
        period: savedPeriod,
      },
      {
        onSuccess: () => { trackSet(true); setEditing(false) },
        onError: (err) => { trackSet(false); onError(err.message) },
      },
    )
  }

  const clear = () => {
    onError(null)
    const trackClear = (ok: boolean) => trackAllowanceAction({
      teamId, action: 'clear', ok, previousLimitType: allowance?.limitType ?? 'no_limit', targetRole, actorRole,
    })
    clearAllowance.mutate(
      { teamId, userId },
      {
        onSuccess: () => { trackClear(true); setEditing(false) },
        onError: (err) => { trackClear(false); onError(err.message) },
      },
    )
  }

  if (!editing) {
    return (
      <button
        type="button"
        data-testid={`teams-allowance-open-${userId}`}
        aria-label={t('settingsModal.teams.allowance.editFor', { name: memberLabel })}
        onClick={() => {
          trackAllowanceAction({ teamId, action: 'edit_opened', previousLimitType: allowance?.limitType ?? 'no_limit', targetRole, actorRole })
          setEditing(true)
        }}
        className="shrink-0 cursor-pointer rounded-md bg-white/[0.08] px-2 py-1 text-xs font-medium text-white/70 transition-colors hover:bg-white/[0.14] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {summary()}
      </button>
    )
  }

  return (
    <div
      data-testid={`teams-allowance-editor-${userId}`}
      className="mt-2 rounded-xl border border-white/[0.08] bg-white/[0.03] px-3 py-2.5"
    >
      <p className="text-xs font-medium text-white/60">{t('settingsModal.teams.allowance.explainer')}</p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <ListboxSelect
          ariaLabel={t('settingsModal.teams.allowance.typeFor', { name: memberLabel })}
          value={limitType}
          options={LIMIT_TYPES}
          disabled={pending}
          labelFor={(v) => t(`settingsModal.teams.allowance.type.${v}`)}
          onSelect={setLimitType}
          triggerTestId={`teams-allowance-type-${userId}`}
          menuTestId={`teams-allowance-type-menu-${userId}`}
          optionTestId={(v) => `teams-allowance-type-option-${userId}-${v}`}
        />

        {limitType !== 'no_limit' && (
          <input
            type="number"
            min={0}
            inputMode="numeric"
            data-testid={`teams-allowance-amount-${userId}`}
            aria-label={t('settingsModal.teams.allowance.amountFor', { name: memberLabel })}
            value={amount}
            disabled={pending}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={t('settingsModal.teams.allowance.amountPlaceholder')}
            // Trigger styling copied from ListboxSelect so the row reads as one
            // set of controls: same height, radius, border and surface.
            // `[appearance:textfield]` and the ::-webkit-* rules drop the native
            // number spinners, which are OS chrome of the same kind the selects
            // just stopped rendering.
            className={cn(
              'h-8 w-28 rounded-lg border border-white/15 bg-white/[0.08] px-2 text-xs font-medium',
              'text-white placeholder:text-white/35 transition-colors',
              'hover:bg-white/[0.14]',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'focus:outline-none focus:ring-2 focus:ring-white/30',
              '[appearance:textfield]',
              '[&::-webkit-outer-spin-button]:appearance-none',
              '[&::-webkit-inner-spin-button]:appearance-none',
            )}
          />
        )}

        {limitType === 'recurring' && (
          <ListboxSelect
            ariaLabel={t('settingsModal.teams.allowance.periodFor', { name: memberLabel })}
            value={period}
            options={PERIODS}
            disabled={pending}
            labelFor={(v) => t(`settingsModal.teams.allowance.period.${v}`)}
            onSelect={setPeriod}
            triggerTestId={`teams-allowance-period-${userId}`}
            menuTestId={`teams-allowance-period-menu-${userId}`}
            optionTestId={(v) => `teams-allowance-period-option-${userId}-${v}`}
          />
        )}
      </div>

      {/* Changing the window resets the counter, so say so before the click
          rather than leaving an admin to discover it from a usage figure. */}
      {allowance !== null &&
        allowance.consumed > 0 &&
        (limitType !== allowance.limitType ||
          (limitType === 'recurring' && period !== allowance.period)) && (
          <p
            data-testid={`teams-allowance-reset-notice-${userId}`}
            className="mt-2 text-xs font-medium leading-5 text-amber-300/80"
          >
            {t('settingsModal.teams.allowance.resetNotice', { consumed: allowance.consumed })}
          </p>
        )}

      <div className="mt-2.5 flex justify-end gap-2">
        {allowance !== null && (
          <button
            type="button"
            data-testid={`teams-allowance-clear-${userId}`}
            disabled={pending}
            onClick={clear}
            className="h-8 cursor-pointer rounded-full px-3 text-xs font-medium text-white/60 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {t('settingsModal.teams.allowance.remove')}
          </button>
        )}
        <button
          type="button"
          data-testid={`teams-allowance-cancel-${userId}`}
          onClick={() => setEditing(false)}
          className="h-8 cursor-pointer rounded-full bg-white/[0.12] px-4 text-xs font-medium text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {t('settingsModal.teams.cancel')}
        </button>
        <button
          type="button"
          data-testid={`teams-allowance-save-${userId}`}
          disabled={pending}
          onClick={submit}
          className={cn(
            'h-8 cursor-pointer rounded-full bg-white px-4 text-xs font-semibold text-black',
            'transition-colors hover:bg-white/90',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          )}
        >
          {t('settingsModal.teams.allowance.save')}
        </button>
      </div>
    </div>
  )
}
